import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { Store, select } from '@ngrx/store'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import * as Extent from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import { Draw, Modify, Select, Snap } from 'ol/interaction'
import FeatureList from 'ol-ext/control/FeatureList'
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
} from 'ol/layer'
import { ImageWMS, Vector as VectorSource } from 'ol/source'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import { Subscription } from 'rxjs'
import { environment } from '@env/environment'
import { Geometry } from 'ol/geom'
import { getUpdateBuildingState } from '@containers/digitization/digitization.selectors'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'layer-map',
  templateUrl: './layer-map.component.html',
  styleUrls: ['./layer-map.component.scss'],
})
export class LayerMapComponent implements OnInit, OnDestroy, AfterViewInit {
  public map: Map
  public draw: Draw
  public _polygonFeature: Draw
  public vector: VectorLayer<any>
  public streetsLayer: VectorLayer<any>
  public snap: Snap
  public overlay: Overlay
  public isPopupVisible: boolean
  public raster: ImageLayer<any>
  public pointFeature: Feature
  public position: number[] = []
  public settings: any
  private settings$: Subscription
  public popupData: any = {}
  public popupDataKeys = []
  public extent: any
  public resolution: any
  public isEditOpen = false
  public featureLoaded = false
  public data: any = {}
  private data$: Subscription
  public selectedFeature: Feature

  @Input() public id: number
  @Input() public color: string
  @Output() public onClose: EventEmitter<any> = new EventEmitter()

  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(
    public store: Store<any>,
    private translateService: TranslateService,
  ) {}

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  ngOnInit() {
    this.data$ = this.store
      .pipe(select(getUpdateBuildingState))
      .subscribe((state) => {
        const data = state.get('data').toJS()

        if (
          data &&
          JSON.stringify(this.data) !== JSON.stringify(data) &&
          this.vector
        ) {
          this.data = data
        }
      })
  }

  ngAfterViewInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })
  }

  ngOnDestroy() {
    this.data$.unsubscribe()
    this.settings$.unsubscribe()
  }

  initMap() {
    const _this = this
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis
    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      offset: [-128, 20],
      autoPan: false,
    })

    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]

    this.raster = new ImageLayer({
      source: new ImageWMS({
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
      }),
    })

    const source = new VectorSource({
      format: new GeoJSON(),
      url: `${environment.API_ENDPOINT}/gis/public/layer/${_this.id}`,
    })

    const styles = {
      MultiPolygon: new Style({
        stroke: new Stroke({
          color: _this.setOpacity(_this.color, 0.5),
          width: 1,
        }),
        fill: new Fill({
          color: _this.setOpacity(_this.color, 0.3),
        }),
      }),
    }

    this.vector = new VectorLayer({
      source,
      style: function (feature: any) {
        return styles[feature.getGeometry().getType()]
      },
    })

    this.map = new Map({
      layers: [this.raster, this.vector],
      target: 'ol-layer',
      overlays: [this.overlay],
      view: new View({
        projection: 'EPSG:4326',
        center: this.position.length
          ? this.position
          : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: 15,
      }),
    })

    this.map.on('pointermove', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as any

      if (feature) {
        const geometry = feature.getGeometry() as any
        const coordinates = geometry.getCoordinates()
        let newCoordinates: any[]

        const { label, value } = feature.values_
        this.popupData = {
          label,
          value,
        }
        this.popupDataKeys = Object.keys(this.popupData)
        this.isPopupVisible = true

        if (coordinates.length === 1) {
          var centroid = Extent.getCenter(geometry.getExtent())
          newCoordinates = centroid
        } else {
          newCoordinates = coordinates
        }

        this.overlay.setPosition(newCoordinates)
      } else {
        this.isPopupVisible = false
      }
    })

    // Select  interaction
    const selectedStyle = new Style({
      fill: new Fill({
        color: 'rgba(50,115,220,0.6)',
      }),
      stroke: new Stroke({
        color: _this.color || 'yellow',
        width: 1,
      }),
    })
    var selectInteraction = new Select({
      hitTolerance: 5,
      style: selectedStyle,
    })
    _this.map.addInteraction(selectInteraction)
    // Select feature when click on the reference index
    selectInteraction.on('select', function (e) {
      var f = e.selected[0]

      _this.selectedFeature = f

      if (f) {
        listCtrl.select(f)
      }
    })

    // Select control
    var listCtrl = new FeatureList({
      title: this.translateService.instant('FEATURES'),
      className: 'ol-features',
      collapsed: false,
      features: source,
    })
    _this.map.addControl(listCtrl)
    listCtrl.on('select' as any, function (e: any) {
      selectInteraction.getFeatures().clear()
      selectInteraction.getFeatures().push(e.feature)
      _this.selectedFeature = e.feature
    })
    listCtrl.on('dblclick' as any, function (e: any) {
      _this.map.getView().fit(e.feature.getGeometry().getExtent())
      _this.map.getView().setZoom(_this.map.getView().getZoom() - 2)
    })

    listCtrl.on(['resize', 'collapse'] as any, function (e) {
      console.log(e)
    })
  }
}
